/**
 * Shared axios client used by every page/component that talks to the
 * FeedPulse backend. Three jobs:
 *
 *   1. Auto-inject `X-FeedPulse-Client: web/<version>` on every request
 *      so backend access logs (and the admin operator dashboard) can
 *      correlate any frontend bug report to the exact build that
 *      produced it. Bump APP_VERSION on every release.
 *
 *   2. Default `validateStatus: () => true` so 4xx/5xx never throw —
 *      callers branch on `r.status` instead. This bypasses production's
 *      Emergent badge script which monkey-patches `window.fetch` to
 *      consume Response bodies before userland can read them. axios
 *      uses XHR under the hood and is immune to that wrapper.
 *
 *   3. Single-retry on 5xx for GET requests only (idempotent). 4xx
 *      never retried — those are deterministic (a 422 won't magically
 *      become 200 on second try, retrying just burns the user's quota).
 *      POST/PUT/PATCH/DELETE never retried because we can't guarantee
 *      they're idempotent on the backend.
 */
import axios from "axios";

// Bump on every release. Backend correlates bug reports to this string.
export const APP_VERSION = "web/v60.15";

const client = axios.create({
  // No baseURL on purpose — every call site already prefixes
  // `process.env.REACT_APP_BACKEND_URL`. Adding a baseURL here would
  // make the prefix double up on the first 14 files we migrate.
  validateStatus: () => true,
  timeout: 60000,
});

client.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  config.headers["X-FeedPulse-Client"] = APP_VERSION;
  return config;
});

const RETRY_BACKOFF_MS = 500;

client.interceptors.response.use(async (response) => {
  const cfg = response.config || {};
  const method = (cfg.method || "get").toLowerCase();
  // v60.79 — VPN / proxy / blacklist / ip-ban detection. Backend sends
  // 403 with one of the codes below; we dispatch a window event so the
  // global <VpnBlockOverlay /> at App root can show the lockout modal.
  // We DO NOT auto-retry these — they need user intervention.
  if (response.status === 403) {
    try {
      const data = response.data;
      const code = (data && (data.error || data?.detail?.error)) || "";
      const BLOCK_CODES = new Set([
        "vpn_detected",
        "tor_exit",
        "hosting_provider",
        "blacklist_spamhaus",
        "ip_banned",
      ]);
      if (BLOCK_CODES.has(code)) {
        try {
          window.dispatchEvent(new CustomEvent("feedpulse:block", { detail: { reason: code } }));
        } catch (_) { /* SSR / no window */ }
      }
    } catch (_) { /* ignore parse errors */ }
  }
  if (response.status >= 500 && method === "get" && !cfg.__fpRetried) {
    cfg.__fpRetried = true;
    await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS));
    return client(cfg);
  }
  return response;
});

export default client;
